// External party portals — Seller "Income & accounting" (PR-B) reads. Party-
// scoped, read-only projection over the converged servicing ledger + seller-
// routed disbursements: a multi-note roll-up + per-note drill-in. Seller
// interest is GROSS (the Phoenix fee/spread carve is not re-derived); the
// broker carve and the buyer consulting fee are each surfaced as their own
// deduction line, because "income received" has already dropped by both.

import { portalApi } from "./client";

/** "current" | "overdue" | "paid-off". */
export type SellerNoteStatus = string;

export interface SellerNoteRow {
  loanId: string;
  loanNumber: string;
  remainingPrincipal: number;
  totalIncomeReceived: number;
  totalBrokerDeductions: number;
  nextDueDate: string | null;
  daysDelinquent: number;
  isOverdue: boolean;
  status: SellerNoteStatus;
}

export interface SellerIncomePoint {
  periodDate: string;
  incomeReceived: number;
}

export interface SellerFinanceSummary {
  noteCount: number;
  totalRemainingPrincipal: number;
  totalIncomeReceived: number;
  totalBrokerDeductions: number;
  nextPaymentDate: string | null;
  anyOverdue: boolean;
  overallStatus: SellerNoteStatus;
  incomeTrend: SellerIncomePoint[];
  notes: SellerNoteRow[];
}

export interface SellerPaymentPeriod {
  periodIndex: number;
  dueDate: string;
  expectedInterest: number;
  expectedPrincipal: number;
  expectedTotal: number;
  incomeReceived: number;
  brokerDeduction: number;
  status: string;
  /** The Buyer Consulting Fee carved out of this period (WF1 P10 monthly +
   *  P11 liquidity, summed) — the broker line's twin. `incomeReceived` is
   *  already net of it. Optional on the FE type so existing fixtures keep
   *  compiling; the server always sends it, 0 where no fee applies. */
  consultingDeduction?: number;
}

export interface SellerNoteLedger {
  loanId: string;
  loanNumber: string;
  remainingPrincipal: number;
  originalPrincipal: number;
  totalIncomeReceived: number;
  totalBrokerDeductions: number;
  nextDueDate: string | null;
  daysDelinquent: number;
  isOverdue: boolean;
  status: SellerNoteStatus;
  sellerInterestIsGross: boolean;
  schedule: SellerPaymentPeriod[];
  incomeTrend: SellerIncomePoint[];
  /** Lifetime Buyer Consulting Fee carved out of this note — the headline twin
   *  of `totalBrokerDeductions`. Optional for the same reason as
   *  `SellerPaymentPeriod.consultingDeduction`. */
  totalConsultingDeductions?: number;
}

// ── Journey-Stage Adaptive Home ──────────────────────────────────────────────

/** "pre-loan" | "note-serviced" | "paid-off" (the C# twin's stage codes). */
export type SellerStageCode = string;

export interface SellerHomeNote {
  loanId: string;
  loanNumber: string;
  remainingPrincipal: number;
  totalIncomeReceived: number;
  nextDueDate: string | null;
  status: SellerNoteStatus;
}

export interface SellerHomeHeroNote {
  loanId: string;
  loanNumber: string;
  status: SellerNoteStatus;
  nextPaymentDate: string | null;
  nextPaymentAmount: number;
  receivedYearToDate: number;
  remainingPrincipal: number;
  totalIncomeReceived: number;
  daysDelinquent: number;
  maturityDate: string | null;
  termMonths: number | null;
  monthsToMaturity: number | null;
  /** State-derived (retired/paid-off loan state) — when true the next-payment
   *  fields are nulled server-side and the FE hides the next-payment tile and
   *  payoff countdown (F-20). */
  isRetired: boolean;
  /** F-03 (locked): nextPaymentAmount/receivedYearToDate are the BUYER's GROSS
   *  payments on the note, not the seller's net distribution. */
  figuresAreGross: boolean;
}

export interface SellerHomePaymentBehavior {
  onTimeCount: number;
  lateCount: number;
  missedCount: number;
  onTimePercent: number; // 0..1 of settled periods
}

export interface SellerHomeDeal {
  dealId: string;
  dealNumber: string;
  businessName: string | null;
  /** Server-resolved seller-friendly stage rendering (e.g. "In underwriting")
   *  — the raw pipeline enum strings don't ride the wire (F-43). */
  stageLabel: string;
  /** How many live deals the seller is on (≥1); pluralizes the pre-note hero.
   *  Optional on the FE type — treated as 1 when absent. */
  dealCount?: number;
}

export interface SellerHome {
  stage: SellerStageCode;
  noteCount: number;
  totalRemainingPrincipal: number;
  totalIncomeReceived: number;
  anyOverdue: boolean;
  overallStatus: SellerNoteStatus;
  notes: SellerHomeNote[];
  heroNote: SellerHomeHeroNote | null;
  paymentBehavior: SellerHomePaymentBehavior | null;
  incomeTrend: SellerIncomePoint[];
  deal: SellerHomeDeal | null;
}

export const portalSellerFinanceApi = {
  /** The seller's stage-adaptive Home dashboard payload. */
  home: () => portalApi.get<SellerHome>("/seller/finance/home"),
  /** Cross-note roll-up across the seller's linked notes. */
  summary: () => portalApi.get<SellerFinanceSummary>("/seller/finance/summary"),
  /** One linked note's full ledger (404 when not linked to this seller). */
  note: (loanId: string) =>
    portalApi.get<SellerNoteLedger>(`/seller/finance/loans/${loanId}`),
  /** On-demand monthly income-statement PDF blob (period = YYYY-MM). */
  statement: (loanId: string, period: string) =>
    portalApi.getBlob(`/seller/finance/loans/${loanId}/statement`, { period }),
};
